import axios, { CancelTokenSource } from "axios";
import { QuantityValidationData } from "components/consultations/UpsertConsultation";
import { DropdownOption } from "components/final-form/dropdownUtils";
import { StyledInput } from "components/final-form/Input";
import { PxDropdownSc } from "components/final-form/Select";
import FormModalContentContainer from "components/layout/FormModalContentContainer";
import PraxFormGrid from "components/layout/PraxFormGrid";
import StyledErrorMessage from "components/layout/StyledErrorMessage";
import ToolbarButton from "components/layout/ToolbarButton";
import {
  EntriesConsumerRenderProps,
  ExtendedBaseEntryDto,
  withEntriesContext
} from "components/segments/entires/EntriesContext";
import React, { Component } from "react";
import { MessageSourceProps, withMessages } from "react-message-source";
import { Button, Grid, Header, Modal, Radio, Table } from "semantic-ui-react";
import {
  distributeBulkQuantities,
  updateTarmedEntrySubPriceAndBastardData,
  validateTarmedQuantity
} from "services/entryServices";
import { QuantityValidationErrorMessage, QuantityValidationErrorType } from "ts-types/api.enums";
import {
  BaseEntryDto,
  DistributeQuantityRequest,
  EntryTable,
  QuantityValidationRequestDto,
  UpdateTarmedEntryDataRequest
} from "ts-types/api.types";
import { ensureOnlyNumericInput } from "utils/filterUtils";
import { numberFormatter } from "utils/fomattingUtils";
import { generateTarmedErrorMessage } from "utils/validatorUtils";

interface Props extends MessageSourceProps, EntriesConsumerRenderProps {
  modalOpen: boolean,
  onCloseModal: () => void,
  tariffId: number,
  tarmedProcedure: ExtendedBaseEntryDto,
  quantityValidationData: QuantityValidationData,
  entryTable: EntryTable,
  validDate: string,
  // code of the descendant that should be pre-selected when the modal opens
  // (e.g. a reference procedure that is entered through this master procedure)
  preselectedChildCode?: string
}

interface State {
  errorMessage?: string,
  tarmedProcedure: ExtendedBaseEntryDto,
  selectedMasterProcedure?: number,
  selectedChildProcedureIndex: number,
  masters: Array<DropdownOption>,
  quantity?: number | string,
  side: string,
  cancelTokenSource: CancelTokenSource
}

class TarmedProcedureModal extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {tarmedProcedure} = this.props;
    const cancelTokenSource = axios.CancelToken.source();

    // TODO ftrajkov 2019-07-10: Implement filtering master procedures from consultation entries as well

    this.state = {
      tarmedProcedure: {...tarmedProcedure},
      selectedChildProcedureIndex: -1,
      masters: [],
      side: "L",
      quantity: "",
      cancelTokenSource
    };
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {

    const {tarmedProcedure: nextTarmedProcedure, preselectedChildCode} = this.props;

    if (nextTarmedProcedure != prevProps.tarmedProcedure && nextTarmedProcedure) {

      const descendants = nextTarmedProcedure.descendants || [];
      const preselectedChildIndex = preselectedChildCode
          ? descendants.findIndex(descendant => descendant.code == preselectedChildCode)
          : -1;

      this.setMasters(nextTarmedProcedure);
      this.setState({
        tarmedProcedure: {...nextTarmedProcedure},
        selectedChildProcedureIndex: preselectedChildIndex,
        quantity: nextTarmedProcedure.units,
        side: nextTarmedProcedure.sides && !!nextTarmedProcedure.side ? nextTarmedProcedure.side : "L",
        errorMessage: undefined
      })
    }
  }

  setMasters = (tarmedProcedure: ExtendedBaseEntryDto) => {

    let masters: Array<DropdownOption> | undefined = undefined;
    if (tarmedProcedure.masters) {

      masters = tarmedProcedure.masters.map((master, index) => {

        let sideText = "";
        if (master.side) {
          sideText = " - " + master.side
        }

        return {
          key: index,
          text: master.code + " - " + master.description + sideText,
          value: index
        }
      });

      this.setState({
        masters: masters
      })
    }
  };

  onMasterSelection = (value: any) => {
    this.setState({
      selectedMasterProcedure: value
    })
  };

  onSideChange = (side: string) => {
    this.setState({
      side: side
    })
  };

  resetErrorMessage = () => {
    this.setState({
      errorMessage: undefined
    })
  };

  markChildProcedure = (index: number) => {

    const {selectedChildProcedureIndex} = this.state;

    if (selectedChildProcedureIndex == -1 || index != selectedChildProcedureIndex) {
      this.setState({
        selectedChildProcedureIndex: index
      })
    }
  };

  onConfirmClicked = () => {

    const {saveTarmedEntry, onCloseModal, getMessage, bastardProcedure, validDate, quantityValidationData} = this.props;
    const {quantity, side, cancelTokenSource} = this.state;

    const result = this.distributeQuantities();

    if (quantity) {
      if (result && (result as Promise<ExtendedBaseEntryDto>).then) {

        result.then(response => {

          this.validateQuantities().then(isValid => {
            if (isValid) {
              const tarmedProcedure: ExtendedBaseEntryDto = {
                ...response,
                units: quantity as number,
                side: response.sides ? side : undefined
              };

              const updateTarmedEntryDataRequest: UpdateTarmedEntryDataRequest = {
                tarmedEntry: tarmedProcedure,
                bastardEntry: bastardProcedure,
                validDate: validDate,
                tariffId: quantityValidationData.tariffId
              };

              updateTarmedEntrySubPriceAndBastardData(updateTarmedEntryDataRequest, cancelTokenSource)
                  .then(response => {
                    if (response) {
                      saveTarmedEntry(response);
                    } else {
                      saveTarmedEntry(tarmedProcedure);
                    }

                    onCloseModal();
                  })
                  .catch(() => {
                    this.setState({
                      errorMessage: getMessage("error.general")
                    })
                  });
            }
          });
        });
      } else {
        onCloseModal();
      }
    } else {
      this.setState({
        errorMessage: getMessage("quantityMustBeHigherThanZero")
      })
    }
  };

  onQuantityChange = (e: any) => {
    this.setState({
      quantity: e.target.value
    })
  };

  addQuantity = () => {
    const {selectedChildProcedureIndex, tarmedProcedure} = this.state;
    const {descendants} = tarmedProcedure;

    if (descendants) {

      const modifiedDescendants = Array.from(descendants);

      modifiedDescendants[selectedChildProcedureIndex] = {
        ...modifiedDescendants[selectedChildProcedureIndex],
        quantity: modifiedDescendants[selectedChildProcedureIndex].quantity + 1
      };

      this.setState({
        tarmedProcedure: {
          ...tarmedProcedure,
          descendants: modifiedDescendants
        }
      })
    }
  };

  subtractQuantity = () => {

    const {selectedChildProcedureIndex, tarmedProcedure} = this.state;
    const {descendants} = tarmedProcedure;

    if (descendants && descendants[selectedChildProcedureIndex].quantity >= 1) {

      const modifiedDescendants = Array.from(descendants);

      modifiedDescendants[selectedChildProcedureIndex] = {
        ...modifiedDescendants[selectedChildProcedureIndex],
        quantity: modifiedDescendants[selectedChildProcedureIndex].quantity - 1
      };

      this.setState({
        tarmedProcedure: {
          ...tarmedProcedure,
          descendants: modifiedDescendants
        }
      })
    }
  };

  distributeQuantities = (): Promise<ExtendedBaseEntryDto> | undefined => {

    const {
      tarmedProcedure,
      quantity,
      cancelTokenSource
    } = this.state;

    const {quantityValidationData: {validDate}, getMessage} = this.props;

    if (tarmedProcedure.bulks) {
      if (quantity && quantity > 0) {

        const bulks: Array<string> = tarmedProcedure.bulks
            .map(bulk => bulk.code)
            .filter(bulk => bulk != tarmedProcedure.code);

        const distributeQuantityRequest: DistributeQuantityRequest = {
          parentCode: tarmedProcedure.code,
          tariffId: this.props.tariffId,
          bulkCodes: bulks,
          validDate: validDate,
          quantity: quantity as number
        };

        return distributeBulkQuantities(distributeQuantityRequest, cancelTokenSource)
            .then(response => {

              const modifiedBulks = tarmedProcedure.bulks!.map(bulk => {

                const quantity = response.distributedQuantities[bulk.code];

                return {
                  ...bulk,
                  quantity: quantity != null ? quantity : 0
                }
              });

              const modifiedTarmedProcedure = {
                ...tarmedProcedure,
                quantity: response.distributedQuantities[tarmedProcedure.code],
                bulks: modifiedBulks
              };

              this.setState({
                tarmedProcedure: modifiedTarmedProcedure,
                errorMessage: undefined
              });

              return modifiedTarmedProcedure;
            })
      } else {
        this.setState({
          errorMessage: getMessage("quantityMustBeHigherThanZero")
        })
      }
    }
  };

  validateQuantities = (): Promise<boolean> => {

    const {
      tarmedProcedure,
      quantity,
      cancelTokenSource
    } = this.state;

    const {
      quantityValidationData,
      getMessage,
      getMessageWithNamedParams,
      entryTable
    } = this.props;

    const quantityValidationRequest: QuantityValidationRequestDto = {
      procedureId: tarmedProcedure.procedureId,
      consultationId: quantityValidationData.consultationId,
      treatmentId: quantityValidationData.treatmentId,
      tariffId: this.props.tariffId,
      patientId: quantityValidationData.patientId,
      validDate: quantityValidationData.validDate,
      units: quantity ? quantity as number : 0,
      tarmedProcedure: tarmedProcedure,
      electronicDataTransfer: quantityValidationData.electronicDataTransfer,
      entryTable: entryTable
    };

    return validateTarmedQuantity(quantityValidationRequest, cancelTokenSource)
        .then(responses => {

          let isValid = true;

          let errorMessage = "";

          responses.forEach(quantityValidationResponse => {

            if (quantityValidationResponse.status == "INVALID") {

              isValid = false;

              const errorMessageKey: QuantityValidationErrorMessage =
                  quantityValidationResponse.errorMessage as QuantityValidationErrorMessage;

              const errorMessageType: QuantityValidationErrorType =
                  quantityValidationResponse.errorType as QuantityValidationErrorType;

              const remainingQuantity = quantityValidationResponse.remainingQuantity;
              const maxQuantity = quantityValidationResponse.maxQuantity;
              const numDays = quantityValidationResponse.constraintQuantity;

              let tarmedErrorMessage = generateTarmedErrorMessage(
                  errorMessageKey, errorMessageType, remainingQuantity, maxQuantity, numDays);

              if (tarmedErrorMessage.maxQuantity) {
                errorMessage += `${quantityValidationResponse.procedureCode}: ${getMessageWithNamedParams(tarmedErrorMessage.messageKey,
                    {
                      maxQuantity: tarmedErrorMessage.maxQuantity,
                      remainingQuantity: tarmedErrorMessage.remainingQuantity,
                      numDays: tarmedErrorMessage.numDays,
                    })};`
              } else {
                errorMessage += `${quantityValidationResponse.procedureCode}: ${getMessage(tarmedErrorMessage.messageKey)};`
              }
            }
          });

          this.setState({
            errorMessage: !isValid ? errorMessage : undefined
          });

          return isValid;
        })
  };

  render(): React.ReactNode {

    const {modalOpen, onCloseModal, getMessage} = this.props;

    return (
        <Modal
            open={modalOpen}
            onClose={onCloseModal}
            closeOnDimmerClick={false}
        >
          <Modal.Header>
            {getMessage("tarmed.requiredEntry")}
          </Modal.Header>
          <FormModalContentContainer>
            {this.renderModalContent()}
          </FormModalContentContainer>
          <Modal.Actions>
            <Button compact primary onClick={this.onConfirmClicked}>{getMessage("button.ok")}</Button>
            <Button compact onClick={onCloseModal}>{getMessage("button.cancel")}</Button>
          </Modal.Actions>
        </Modal>
    );
  }

  renderModalContent = (): React.ReactNode => {

    const {unitText, getMessage} = this.props;

    const {
      selectedChildProcedureIndex,
      tarmedProcedure: {bulks = [], descendants = []} = {
        bulks: [],
        descendants: []
      }
    } = this.state;

    const {
      quantity,
      side,
      masters,
      errorMessage,
      tarmedProcedure
    } = this.state;

    return (
        <PraxFormGrid>
          {errorMessage && <StyledErrorMessage
              onDismiss={() => this.resetErrorMessage()}>{this.renderErrorMessage()}</StyledErrorMessage>}
          <Grid.Row>
            <Grid.Column verticalAlign="middle" width={4}>
              {unitText ? unitText : `${getMessage("quantity")}:`}
            </Grid.Column>
            <Grid.Column verticalAlign="middle" width={12}>
              <StyledInput value={quantity != null ? quantity : ""} onChange={this.onQuantityChange}
                           onKeyDown={ensureOnlyNumericInput} fluid/>
            </Grid.Column>
          </Grid.Row>
          {
              tarmedProcedure.masters && tarmedProcedure.masters.length > 0 &&
              <Grid.Row>
                <Grid.Column verticalAlign="middle" width={4}>{getMessage("parentProcedure")}:</Grid.Column>
                <Grid.Column verticalAlign="middle" width={12}>
                  <PxDropdownSc
                      onChange={(e: any, data: any) => this.onMasterSelection(data.value)}
                      options={masters}
                      selection
                      search
                      fluid
                  />
                </Grid.Column>
              </Grid.Row>
          }
          {
              tarmedProcedure.sides &&
              <Grid.Row>
                <Grid.Column verticalAlign="middle" width={4}>{getMessage("side")}:</Grid.Column>
                <Grid.Column verticalAlign="middle" width={12}>
                  <Radio
                      onClick={() => this.onSideChange("L")}
                      label={getMessage("left")}
                      style={{width: "70px"}}
                      checked={side == "L"}
                      name={"sides"}
                  />
                  <Radio
                      onClick={() => this.onSideChange("R")}
                      label={getMessage("right")}
                      checked={side == "R"}
                      name={"sides"}
                  />
                </Grid.Column>
              </Grid.Row>
          }
          <Grid.Row>
            <Grid.Column width={13}>
              <Header>{getMessage("procedure.bulks")}</Header>
            </Grid.Column>
            <Grid.Column textAlign="right" width={3}>
              <Button compact primary onClick={this.distributeQuantities}>{getMessage("distribute")}</Button>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            {this.renderTableForData(bulks, false)}
          </Grid.Row>
          {
              descendants && descendants.length > 0 &&
              <React.Fragment>
                <Grid.Row>
                  <Grid.Column width={13}>
                    <Header>{getMessage("procedure.descendants")}</Header>
                  </Grid.Column>
                  <Grid.Column width={3} textAlign="right">
                    <ToolbarButton iconName="plus"
                                   iconSize="large"
                                   onClick={this.addQuantity}
                                   disabled={selectedChildProcedureIndex == -1}
                                   dataTooltip={getMessage('plusQuantity')}
                    />
                    <ToolbarButton iconName="minus"
                                   iconSize="large"
                                   onClick={this.subtractQuantity}
                                   disabled={selectedChildProcedureIndex == -1}
                                   dataTooltip={getMessage('minusQuantity')}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  {this.renderTableForData(descendants, true)}
                </Grid.Row>

              </React.Fragment>
          }
        </PraxFormGrid>
    )
  };

  renderTableForData = (entries: Array<BaseEntryDto>, canMark: boolean): React.ReactNode => {

    const {getMessage} = this.props;
    const {selectedChildProcedureIndex} = this.state;

    const mappedEntries = entries.map((entry, index) => {
      return (
          <Table.Row
              key={index}
              onClick={canMark ? (() => this.markChildProcedure(index)) : undefined}
              active={canMark && selectedChildProcedureIndex == index}
              style={{color: entry.quantity == 0 ? "lightgray" : "black", userSelect: "none"}}
          >
            <Table.Cell textAlign="right">{numberFormatter.format(entry.quantity)}</Table.Cell>
            <Table.Cell>{entry.side}</Table.Cell>
            <Table.Cell>{entry.code}</Table.Cell>
            <Table.Cell>{entry.description}</Table.Cell>
          </Table.Row>
      )
    });

    return (
        <Table basic="very" celled selectable={canMark} style={{marginLeft: "10px", marginRight: "10px"}}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell verticalAlign={"middle"}>{getMessage("procedure.quantity.short")}</Table.HeaderCell>
              <Table.HeaderCell verticalAlign={"middle"}>{getMessage('procedure.leftRight')}</Table.HeaderCell>
              <Table.HeaderCell verticalAlign={"middle"}>{getMessage('code')}</Table.HeaderCell>
              <Table.HeaderCell verticalAlign={"middle"}>{getMessage('description')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {mappedEntries}
          </Table.Body>
        </Table>
    );
  };

  renderErrorMessage = (): React.ReactNode | string => {

    const {errorMessage = ""} = this.state;

    const splitErrors = errorMessage.split(";");

    if (splitErrors.length > 1) {
      return (
          <React.Fragment>
            {splitErrors.map((error, index) => <div key={index}>{error}</div>)}
          </React.Fragment>
      )
    }

    return errorMessage;
  }
}

export default withMessages(withEntriesContext(TarmedProcedureModal));
